import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getStripeConnectDashboardUrl } from '../../lib/api';
import { Button } from '../ui';
import Badge from '../ui/Badge';

/**
 * SellerTab - Stripe Connect and seller settings
 *
 * Shows:
 * - Stripe Connect status
 * - Link to Stripe Express dashboard
 * - Seller stats summary
 */
export default function SellerTab() {
  const { userProfile, hasStripeConnect, stripeConnectPending } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const handleOpenStripeDashboard = async () => {
    setLoading(true);
    try {
      const { url } = await getStripeConnectDashboardUrl();
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error getting Stripe dashboard URL:', error);
      showToast({
        title: 'Error',
        description: 'Could not open Stripe dashboard. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Stripe Connect Status */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Payment Setup</h3>
        <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#635bff]/10 flex items-center justify-center shrink-0">
              <StripeIcon className="w-6 h-6 text-[#635bff]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-semibold text-cream">Stripe Connect</h4>
                <ConnectStatusBadge
                  status={hasStripeConnect ? 'active' : stripeConnectPending ? 'pending' : 'not_connected'}
                />
              </div>
              <p className="text-sm text-cream/50 mb-4">
                {hasStripeConnect
                  ? 'Your Stripe Express account is active. You can receive payouts from competitions.'
                  : stripeConnectPending
                  ? 'Your account is pending verification. Complete the onboarding to start receiving payouts.'
                  : 'Connect your Stripe account to receive payouts when buyers collect items.'}
              </p>

              {hasStripeConnect ? (
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenStripeDashboard}
                    loading={loading}
                  >
                    <ExternalLinkIcon className="w-4 h-4 mr-2" />
                    Open Stripe Dashboard
                  </Button>
                </div>
              ) : stripeConnectPending ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => window.location.href = '/seller/onboarding'}
                >
                  Complete Setup
                </Button>
              ) : (
                <Button
                  variant="accent"
                  size="sm"
                  onClick={() => window.location.href = '/seller/onboarding'}
                >
                  Set Up Payments
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payout Details */}
      {hasStripeConnect && (
        <div>
          <h3 className="text-lg font-semibold text-cream mb-4">Payout Information</h3>
          <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-cream/60">Payout Schedule</span>
                <span className="text-cream">Daily (automatic)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-cream/60">Payout Method</span>
                <span className="text-cream">Bank Account (via Stripe)</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-cream/60">Processing Time</span>
                <span className="text-cream">2-3 business days</span>
              </div>
            </div>
            <p className="text-xs text-cream/40 mt-4">
              Payouts are processed through Stripe Express. Funds are released after handover confirmation.
            </p>
          </div>
        </div>
      )}

      {/* Platform Fees */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Platform Fees</h3>
        <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-cream">Listing Fee</span>
                <p className="text-xs text-cream/40">Per competition</p>
              </div>
              <span className="text-success font-mono font-semibold">Free</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-cream">Platform Fee</span>
                <p className="text-xs text-cream/40">On successful sales only</p>
              </div>
              <span className="text-cream font-mono font-semibold">5%</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-cream">Payment Processing</span>
                <p className="text-xs text-cream/40">Stripe fees</p>
              </div>
              <span className="text-cream font-mono">~1.5% + €0.25</span>
            </div>
          </div>
        </div>
      </div>

      {/* Seller Guidelines */}
      <div>
        <h3 className="text-lg font-semibold text-cream mb-4">Seller Guidelines</h3>
        <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 p-6">
          <ul className="space-y-3 text-sm text-cream/70">
            <li className="flex items-start gap-3">
              <CheckIcon className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <span>You can have one active competition at a time</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckIcon className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <span>Photos must accurately represent your item</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckIcon className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <span>Items must be available for local pickup in Ireland</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckIcon className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <span>Complete handover within 7 days of draw</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckIcon className="w-5 h-5 text-success shrink-0 mt-0.5" />
              <span>Respond to winner messages within 24 hours</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function ConnectStatusBadge({ status }) {
  const config = {
    active: { label: 'Active', variant: 'success' },
    pending: { label: 'Pending', variant: 'warning' },
    not_connected: { label: 'Not Connected', variant: 'default' },
  };

  const { label, variant } = config[status] || config.not_connected;

  return <Badge variant={variant} size="sm">{label}</Badge>;
}

// Icons
function StripeIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
    </svg>
  );
}

function ExternalLinkIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function CheckIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
